// src/pages/PatientInvoicesPage.jsx
// Route: /patient/invoices
//
// Consolidated, downloadable invoices for a patient's paid-for items — lab
// test orders, doctor appointments, and their current subscription plan.
// No new backend surface: reuses the unified wallet ledger (every purchase
// entry, wallet-paid or gateway-only, already carries referenceType/
// referenceId — see WalletLedgerEntryDTO) to find which orders/appointments
// are invoiceable, and the same per-item downloadInvoice endpoints
// PatientAppointmentsPage, PatientLabTestOrderDetailPage and
// PatientSubscriptionPage already call.

import React, { useEffect, useState, useCallback } from 'react';
import { PatientAppShell } from '../components/PatientLayout';
import WalletBillingTabs from '../components/WalletBillingTabs';
import { usePatient } from '../context/PatientContext';
import { useToast } from '../context/ToastContext';
import { walletAPI, appointmentAPI, labTestOrderAPI, patientPlanAPI } from '../services/api';

const PAGE_SIZE = 50;

function formatRupees(minorUnits) {
  return (minorUnits / 100).toLocaleString('en-IN', {
    style: 'currency', currency: 'INR', maximumFractionDigits: 0,
  });
}

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-IN', {
    day: 'numeric', month: 'short', year: 'numeric',
  });
}

const REFERENCE_LABELS = {
  LAB_ORDER:   'Lab test order',
  APPOINTMENT: 'Doctor appointment',
};

// A single order/appointment paid partly via wallet and partly via Razorpay
// produces TWO ledger rows referencing the same (referenceType, referenceId)
// — a real WALLET_DEBIT_* row for the wallet portion and a synthetic
// RAZORPAY_* row for the gateway portion (see WalletLedgerEntryDTO's
// javadoc). Only one invoice exists per underlying order/appointment, so
// entries are grouped — and their amounts summed — before rendering.
function groupInvoiceable(entries) {
  const groups = new Map();
  entries
    .filter(e => e.referenceType === 'LAB_ORDER' || e.referenceType === 'APPOINTMENT')
    .forEach(e => {
      const key = `${e.referenceType}:${e.referenceId}`;
      const existing = groups.get(key);
      if (existing) {
        existing.amountMinorUnits += e.amountMinorUnits;
        if (new Date(e.createdAt) > new Date(existing.createdAt)) existing.createdAt = e.createdAt;
      } else {
        groups.set(key, { ...e });
      }
    });
  return Array.from(groups.values()).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
}

function InvoiceRow({ entry, patientId, toast }) {
  const [downloading, setDownloading] = useState(false);

  async function handleDownload() {
    setDownloading(true);
    try {
      const res = entry.referenceType === 'LAB_ORDER'
        ? await labTestOrderAPI.downloadInvoice(patientId, entry.referenceId)
        : await appointmentAPI.downloadInvoice(patientId, entry.referenceId);
      const win = window.open(res.data.downloadUrl, '_blank', 'noopener');
      if (!win) toast.error('Pop-up blocked — please allow pop-ups for this site and try again.');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not generate an invoice for this item.');
    } finally {
      setDownloading(false);
    }
  }

  return (
    <div style={{
      display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 10,
      padding: '12px 0', borderBottom: '1px solid var(--bg-subtle)',
    }}>
      <div style={{ flex: '1 1 160px', minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-primary)' }}>
          {REFERENCE_LABELS[entry.referenceType] || entry.referenceType}
        </div>
        <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
          {formatDate(entry.createdAt)} · {formatRupees(entry.amountMinorUnits)}
        </div>
      </div>
      <button
        type="button"
        className="btn btn-secondary btn-sm"
        onClick={handleDownload}
        disabled={downloading}
      >
        {downloading ? 'Generating…' : '⬇ Download invoice'}
      </button>
    </div>
  );
}

export default function PatientInvoicesPage() {
  const { patient, plan } = usePatient();
  const { toast } = useToast();

  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [downloadingPlan, setDownloadingPlan] = useState(false);

  const patientId = patient?.id;

  const load = useCallback(async () => {
    if (!patientId) return;
    setLoading(true);
    setError(false);
    try {
      const res = await walletAPI.getTransactions(patientId, 0, PAGE_SIZE);
      setEntries(res.data.transactions || []);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [patientId]);

  useEffect(() => { load(); }, [load]);

  async function handleDownloadPlanInvoice() {
    if (!patientId) return;
    setDownloadingPlan(true);
    try {
      const res = await patientPlanAPI.downloadInvoice(patientId);
      const win = window.open(res.data.downloadUrl, '_blank', 'noopener');
      if (!win) toast.error('Pop-up blocked — please allow pop-ups for this site and try again.');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not generate an invoice for your plan.');
    } finally {
      setDownloadingPlan(false);
    }
  }

  const invoiceable = groupInvoiceable(entries);

  // Same gating PatientSubscriptionPage uses for its own "Download Invoice"
  // button: isPaid is true for both a self-paid active plan and one covered
  // by a subscriber, and only the former has a patient-owned plan invoice to
  // generate — a subscriber-covered patient never bought a plan themselves.
  const showPlanInvoice = plan?.isPaid && !plan.coveredBySubscriber;

  return (
    <PatientAppShell breadcrumbs={[{ label: 'Wallet & Billing', to: '/patient/wallet' }, { label: 'Invoices' }]}>
      <div style={{
        maxWidth: 560, margin: '0 auto',
        padding: '24px 0 48px',
        fontFamily: 'var(--font-body)',
      }}>
        <WalletBillingTabs />

        <div style={{
          fontSize: 12, fontWeight: 600, textTransform: 'uppercase',
          letterSpacing: '.1em', color: 'var(--text-muted)', marginBottom: 4,
        }}>
          Previu Billing
        </div>
        <div style={{ fontSize: 20, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 24 }}>
          Invoices
        </div>

        {showPlanInvoice && (
          <div style={{
            background: 'var(--bg-surface)', border: '1px solid var(--border)',
            borderRadius: 'var(--radius-lg)', padding: '20px 24px', marginBottom: 20,
          }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 12 }}>
              Your plan
            </div>
            <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
              <div style={{ flex: '1 1 160px', minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-primary)' }}>
                  {plan.label}
                </div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{plan.detail}</div>
              </div>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={handleDownloadPlanInvoice}
                disabled={downloadingPlan}
              >
                {downloadingPlan ? 'Generating…' : '⬇ Download invoice'}
              </button>
            </div>
          </div>
        )}

        <div style={{
          background: 'var(--bg-surface)', border: '1px solid var(--border)',
          borderRadius: 'var(--radius-lg)', padding: '20px 24px',
        }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 14 }}>
            Lab tests &amp; appointments
          </div>

          {loading && (
            <div style={{ textAlign: 'center', padding: '20px 0', color: 'var(--text-muted)', fontSize: 14 }}>
              Loading your invoices…
            </div>
          )}

          {!loading && error && (
            <div style={{ fontSize: 13, color: 'var(--red)' }}>
              Couldn't load your invoices. Please try again.
            </div>
          )}

          {!loading && !error && invoiceable.length === 0 && (
            <div style={{ fontSize: 13, color: 'var(--text-muted)', fontStyle: 'italic' }}>
              No invoices yet.
            </div>
          )}

          {!loading && !error && invoiceable.map(entry => (
            <InvoiceRow
              key={`${entry.referenceType}:${entry.referenceId}`}
              entry={entry}
              patientId={patientId}
              toast={toast}
            />
          ))}
        </div>
      </div>
    </PatientAppShell>
  );
}
